import { useCallback } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useSelector } from "react-redux";
import { type StringIndexedObject, useForm } from "@kanda-libs/ks-component-ts";
import { useToast } from "@kanda-libs/ks-design-library";
import { actions } from "@kanda-libs/ks-frontend-services";
import { REASONS } from "components/company/CompanyDeclineModal/constants";
import companySelectors from "store/selectors/company";
import useReloadInitialData from "hooks/useReloadInitialData";
import { useAppDispatch } from "components/shared/App";
import type { ValidReason } from "./types";

export interface DeclineCompanyFormHook {
  form: UseFormReturn;
  isSubmitting: boolean;
  options: { name: string; value: string }[];
  onDecline: (reason: ValidReason) => void;
  onSubmit: (formValues: StringIndexedObject<string>) => void;
}

export default function useDeclineCompanyForm(
  handleClose: () => void
): DeclineCompanyFormHook {
  const dispatch = useAppDispatch();
  const isSubmitting = useSelector(companySelectors.getIsSubmitting);
  const company = useSelector(companySelectors.getEntity);

  const form = useForm({ mode: "onBlur" });

  const refreshAll = useReloadInitialData();
  const { showSuccess, showError } = useToast();

  const options = REASONS.map((reason) => ({
    name: reason.replace(/_/g, " ").replace(" ", " - "),
    value: reason,
  }));

  const onError = () => {
    handleClose();
    showError("Error approving company");
  };

  const onDecline = useCallback(
    (reason: ValidReason) => {
      const id = company?.id || "";

      if (!reason) {
        showError("Invalid reason provided");
      }

      dispatch(
        actions.declineCompany({
          params: { id, reason },
          onError,
          onSuccess: () => {
            showSuccess("Company declined");
            handleClose();
          },
        })
      );
    },
    [
      company,
      showError,
      showSuccess,
      actions.declineCompany,
      handleClose,
      refreshAll,
    ]
  );

  const onSubmit = useCallback(
    (formValues: StringIndexedObject<string>) => {
      onDecline(formValues?.reason as ValidReason);
    },
    [onDecline]
  );

  return {
    form,
    isSubmitting,
    options,
    onDecline,
    onSubmit,
  };
}
